import React, { useMemo } from 'react';
import { Typography, Checkbox, Box } from '@mui/material';
import CustomAccordion from '../ui/CustomAccordion';
import DynamicTable, { DynamicColumn } from '../ui/DynamicTable';
import { CdpResourceTableResetBar, useCdpResourceTable } from './cdpResourceTableSupport';

interface SyncedMedication {
  leadId?: string;
  id?: number;
  value: string;
  codingSystem: string;
  synced: boolean;
}

interface MedicationsInCDPProps {
  selectedItems: Set<string>;
  onCheckboxChange: (itemId: string, itemType: string) => void;
  showSyncColumn?: boolean;
  syncedData?: SyncedMedication[];
  fieldConfigs?: any;
  headerText?: string;
  syncText?: string;
}

const MedicationsInCDPSection: React.FC<MedicationsInCDPProps> = ({
  selectedItems,
  onCheckboxChange,
  showSyncColumn = true,
  syncedData,
  fieldConfigs,
  headerText,
  syncText,
}) => {
  const [expanded, setExpanded] = React.useState(true);

  const defaultColumns = useMemo(
    () => [
      { key: 'id', label: 'Code', width: showSyncColumn ? '14%' : '16.5%' },
      { key: 'value', label: 'Value', width: showSyncColumn ? '39%' : '46%' },
      { key: 'codingSystem', label: 'Coding System', width: 'auto' },
    ],
    [showSyncColumn]
  );

  const configuredColumns = useMemo(() => {
    if (!fieldConfigs || !Array.isArray(fieldConfigs)) return defaultColumns;
    return fieldConfigs
      .map((config: any) => ({
        key: config?.fields?.ExpandedViewItemKey?.value || '',
        label: config?.fields?.ExpandedViewItemLabel?.value || '',
        width: config?.fields?.ExpandedViewItemWidth?.value || 'auto',
      }))
      .filter((item) => item.key && item.label);
  }, [fieldConfigs, defaultColumns]);

  const baseColumns: DynamicColumn<SyncedMedication>[] = useMemo(() => {
    const leadIdColumn: DynamicColumn<SyncedMedication> = {
      id: 'leadId',
      label: 'Lead Id',
      accessor: 'leadId',
      width: 'auto',
      render: (row) => (
        <Typography sx={{ fontSize: '12px' }}>{row.leadId?.trim() ? row.leadId : '-'}</Typography>
      ),
    };

    return [
      leadIdColumn,
      ...configuredColumns.map((config) => ({
        id: config.key,
        width: config.width || 'auto',
        label: config.label,
        accessor: config.key as keyof SyncedMedication,
        render: (row: SyncedMedication) => (
          <Typography sx={{ fontSize: '12px' }}>{(row as any)[config.key] || '-'}</Typography>
        ),
      })),
      ...(showSyncColumn
        ? [
            {
              id: 'sync',
              label: syncText || 'Sync',
              render: (row: SyncedMedication) => (
                <Box sx={{ textAlign: 'left' }}>
                  {row.synced ? (
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '11px', color: '#4caf50', fontWeight: 500 }}
                    >
                      Synced
                    </Typography>
                  ) : (
                    <Checkbox
                      checked={selectedItems.has(`medication-${row.id || ''}`)}
                      onChange={() => onCheckboxChange((row.id || '').toString(), 'medication')}
                      size="small"
                      sx={{
                        color: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
                        '&.Mui-checked': {
                          color: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
                        },
                      }}
                    />
                  )}
                </Box>
              ),
            } as DynamicColumn<SyncedMedication>,
          ]
        : []),
    ];
  }, [configuredColumns, showSyncColumn, selectedItems, onCheckboxChange, syncText]);

  const { columns, tableData, hasActiveFilterOrSort, onResetFilters } = useCdpResourceTable(
    baseColumns,
    syncedData
  );

  if (!syncedData || syncedData.length === 0) return null;

  return (
    <CustomAccordion
      title={`${headerText || 'Medications in CDP'} (${syncedData.length})`}
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        mt: 1.5,
        '& .MuiAccordionSummary-root': {
          minHeight: '40px',
          py: 0.5,
          backgroundColor: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
          color: 'white',
          borderRadius: '4px 4px 0 0',
        },
        '& .MuiAccordionSummary-root.Mui-expanded': {
          minHeight: '40px',
          py: 0.5,
        },
        '& .MuiAccordionSummary-content': {
          margin: '0 !important',
          color: 'white !important',
        },
        '& .MuiAccordionSummary-content.Mui-expanded': {
          margin: '0 !important',
        },
        '&:before': { display: 'none' },
        '& .MuiAccordionDetails-root': {
          p: 0,
          borderColor: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
          border: '2px solid',
          borderTop: 'none',
        },
      }}
      summarySx={{
        backgroundColor: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
        color: 'white',
        '& .MuiTypography-root': {
          color: 'white',
          fontSize: '15px',
          fontWeight: 400,
        },
        '& .MuiSvgIcon-root': {
          color: 'white',
        },
      }}
      detailsSx={{
        p: 0,
        borderColor: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
        border: '2px solid',
        borderTop: 'none',
      }}
    >
      <CdpResourceTableResetBar hasActive={hasActiveFilterOrSort} onReset={onResetFilters} />
      <DynamicTable
        columns={columns}
        data={tableData}
        variant="compact"
        noDataMessage="No medications found"
        getRowId={(row) => `${row.leadId ?? ''}-${row.id ?? ''}-${row.value}-${row.codingSystem}`}
      />
    </CustomAccordion>
  );
};

export default MedicationsInCDPSection;
